import { useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Search, Trash2, Loader2, AlertTriangle, Shapes as ShapesIcon, Download, Upload, Pencil, Plus } from 'lucide-react'
import { shapesApi, SHAPE_ACCEPT, graphicName, type ShapeItem, type ShapeUsage } from '@/features/shapes/api/shapes'
import { GraphicUploadForm } from '../components/GraphicUploadForm'
import { SizeInfo } from '../components/SizeInfo'
import { drawnBox, isVectorFilename, recommendedPixels, sharpness, sharpUpToZoom } from '@/features/shapes/lib/graphic-resolution'
import { ImageIntake } from '@/components/shared/ImageIntake'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { buildShapePath } from '@/lib/imagePaths'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import { EntityIcon } from '@/components/ui/entity-icon'
import { useCan } from '@/hooks/useCan'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'

/**
 * How far the canvas can zoom before a graphic blurs, or null when that is not a question: a
 * vector, a graphic with no recorded size, or one whose image has not loaded yet.
 *
 * The file's PIXELS come from the image on screen, not from the database. The card is already
 * showing the artwork, so its own size is free and, unlike a stored number, cannot go stale if a
 * file is ever replaced in the bucket. Its SIZE — what the canvas draws — is the stored width and
 * height; the two have been separate since 2026-09-22 (lib/graphic-resolution.ts).
 */
function blurZoom(shape: ShapeItem, pixels?: { width: number; height: number }): number | null {
    if (isVectorFilename(shape.filename) || !pixels || !shape.width || !shape.height) return null
    return sharpUpToZoom(pixels, { width: shape.width, height: shape.height })
}

/**
 * The image URL with a version that changes whenever this page replaces the file.
 *
 * A replacement keeps the filename — that is the point, everything using the graphic picks it up —
 * so it also keeps the URL, and a browser that already has an image for a URL does not fetch it
 * again within the same page. The card went on showing the old artwork, and the blur comment,
 * which is worked out from the pixels of the image as loaded, went on describing the old file.
 * Andrew, 2026-09-24: "It's not updating the comment about being blurred." The server and the CDN
 * were both already serving the new file (max-age=0, must-revalidate); only the page was stale.
 */
function versioned(url: string, version?: number): string {
    if (!url || !version) return url
    return `${url}${url.includes('?') ? '&' : '?'}v=${version}`
}

const formatZoom = (z: number, lang: string) => (Math.floor(z * 10) / 10).toLocaleString(lang, { maximumFractionDigits: 1 })

// Human-readable label for the shape, matching how the icon grid presents its own.
export function ShapesAdminPage() {
    const { t, i18n } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    // The screen is open to every graphics.read; a change is offered only where the API allows it.
    // An upload - bulk, add, or replacing a graphic's image - is graphics.create; editing its
    // description, category and tags is graphics.update.
    const canCreate = useCan('graphics.create')
    const canUpdate = useCan('graphics.update')
    const canDelete = useCan('graphics.delete')
    const [search, setSearch] = useState('')
    // One control, two jobs, and the button label says so: it narrows the grid AND it is
    // the category a bulk upload lands in. Keeping them separate would mean two category
    // dropdowns side by side, and the question "which category am I working in" is really
    // only asked once.
    const [workingCategory, setWorkingCategory] = useState<string>('')

    const [deleteTarget, setDeleteTarget] = useState<ShapeItem | null>(null)
    const [editTarget, setEditTarget] = useState<ShapeItem | null>(null)
    const [editDescription, setEditDescription] = useState('')
    const [editTags, setEditTags] = useState('')
    // Each graphic's own pixels, as its card's image reports them on load. Keyed by shape id.
    const [loadedPixels, setLoadedPixels] = useState<Record<number, { width: number; height: number }>>({})
    // Bumped per filename when this page replaces a file, so the card's image URL changes and the
    // browser fetches the new file (see versioned()).
    const [imageVersion, setImageVersion] = useState<Record<string, number>>({})

    // After a replacement: forget the old file's pixels — so no blur comment is shown for a file
    // that is no longer there — and give the image a new URL so the new one loads and reports its own.
    const imageReplaced = (filename: string) => {
        setImageVersion((v) => ({ ...v, [filename]: Date.now() }))
        const id = shapes.find((s) => s.filename === filename)?.id
        if (id !== undefined) {
            setLoadedPixels((seen) => {
                if (!(id in seen)) return seen
                const { [id]: _stale, ...rest } = seen
                return rest
            })
        }
    }
    const [exporting, setExporting] = useState(false)
    const [uploading, setUploading] = useState(false)
    const fileInput = useRef<HTMLInputElement>(null)

    const { data: shapes = [], isLoading } = useQuery({
        queryKey: ['shapes'],
        queryFn: async () => {
            const res = await shapesApi.list()
            return res.data.data
        },
    })

    const { data: categories = [] } = useQuery({
        queryKey: ['shape-categories'],
        queryFn: async () => (await shapesApi.categories()).data.data,
        staleTime: 5 * 60 * 1000,
    })

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase()
        const inCategory = workingCategory
            ? shapes.filter((s) => s.category === workingCategory)
            : shapes
        if (!term) return inCategory
        return inCategory.filter(
            (s) =>
                s.filename?.toLowerCase().includes(term) ||
                s.description?.toLowerCase().includes(term) ||
                s.tags?.toLowerCase().includes(term),
        )
    }, [shapes, search, workingCategory])

    // The whole library, not the filtered grid: the search box narrows what is on screen, and an
    // export that silently honoured it would hand over a subset that looks complete.
    const handleExport = async () => {
        setExporting(true)
        try {
            const response = await fetch('/api/shapes/export', { headers: authHeaders() })
            if (!response.ok) throw new Error(`Export failed (${response.status})`)
            // Through a blob rather than a plain link: the request needs the auth header.
            const blob = await response.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `VaultSentinelGraphics-${new Date().toISOString().slice(0, 10)}.zip`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch (e: any) {
            toast({
                title: t('shapes.exportFailed', 'Export failed'),
                description: e?.message || String(e),
                variant: 'destructive',
            })
        } finally {
            setExporting(false)
        }
    }

    // Upload lives on this screen, unlike icons, which are uploaded from the object picker.
    // Shapes have no picker yet — this is the only place they can be added, so without it the
    // library would have no way to get its first file.
    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return
        setUploading(true)
        let added = 0
        const failures: string[] = []
        try {
            const clashes: { file: File; category?: string }[] = []
            for (const file of Array.from(files)) {
                try {
                    // Drawn 2 cm high, whatever the file's resolution (lib/graphic-resolution.ts).
                    await shapesApi.upload(file, undefined, undefined, false, workingCategory)
                    added++
                } catch (err: any) {
                    // 409 means the filename is already in the library. COLLECTED, not asked
                    // one at a time: a native prompt per clashing file means a drop of twenty
                    // files can stack twenty dialogs, and the answer is nearly always the same
                    // for all of them. They are put to the user once, in the app's own dialog,
                    // after everything that could be added has been.
                    if (err?.response?.status === 409) clashes.push({ file, category: workingCategory })
                    else failures.push(`${file.name}: ${err?.response?.data?.message || err?.message || 'failed'}`)
                }
            }
            if (clashes.length > 0) setBulkClashes(clashes)
            if (added > 0) queryClient.invalidateQueries({ queryKey: ['shapes'] })
            if (added > 0 && failures.length === 0) {
                toast({
                    title: t('shapes.uploadedTitle', 'Graphics uploaded'),
                    description: t('shapes.uploadedDescription', '{{count}} graphic added to the library.', { count: added }),
                })
            }
            // Reported rather than swallowed: a partial upload that looks like a success is how
            // someone ends up wondering why a shape is missing later.
            if (failures.length > 0) {
                toast({
                    variant: 'destructive',
                    title: t('shapes.uploadPartial', 'Some graphics were not added'),
                    description: failures.join(' · '),
                })
            }
        } finally {
            setUploading(false)
            if (fileInput.current) fileInput.current.value = ''
        }
    }

    // ── Add a shape ──────────────────────────────────────────────────────────
    // Same shape of interaction as Add icon: one dialog that takes the image from a
    // file OR the clipboard, shows what it got, and collects the metadata BEFORE
    // anything is written. The bulk button beside it still exists for dropping in a
    // folder of files at once, but that path cannot ask for a description per file,
    // which is exactly what the search box later needs.
    const [editCategory, setEditCategory] = useState<string>('')
    const [addOpen, setAddOpen] = useState(false)

    const closeAdd = () => setAddOpen(false)

    // Files a bulk drop could not add because the name is taken. Held until the user answers.
    const [bulkClashes, setBulkClashes] = useState<{ file: File; category?: string }[]>([])

    const replaceClashes = async () => {
        const list = bulkClashes
        setBulkClashes([])
        setUploading(true)
        let replaced = 0
        const failures: string[] = []
        try {
            for (const item of list) {
                try {
                    await shapesApi.upload(item.file, undefined, undefined, true, item.category)
                    imageReplaced(item.file.name)
                    replaced++
                } catch (e: any) {
                    failures.push(`${item.file.name}: ${e?.response?.data?.message || e?.message || 'failed'}`)
                }
            }
            if (replaced > 0) {
                queryClient.invalidateQueries({ queryKey: ['shapes'] })
                toast({
                    title: t('shapes.replacedTitle', 'Graphic image replaced'),
                    description: t('shapes.uploadedDescription', '{{count}} graphic added to the library.', { count: replaced }),
                })
            }
            if (failures.length > 0) {
                toast({
                    variant: 'destructive',
                    title: t('shapes.uploadPartial', 'Some graphics were not added'),
                    description: failures.join('\n'),
                })
            }
        } finally {
            setUploading(false)
        }
    }

    // A single image, from a paste or from the edit dialog's file button.
    //
    // Kept apart from handleFiles because the two answer different questions.
    // handleFiles is a bulk add: several files, each of which MIGHT clash, so each
    // clash is a question for the user. Here the filename is either brand new (a
    // paste, which has no name of its own) or deliberately the existing one (a
    // replace, where overwriting is the entire point), so there is nothing to ask.
    const uploadSingle = async (file: File, overwrite: boolean, category?: string) => {
        setUploading(true)
        try {
            const saved = await shapesApi.upload(file, undefined, undefined, overwrite, category)
            if (overwrite) {
                imageReplaced(saved.filename)
                // The dialog holds the row it was opened with. The server has just re-measured the
                // file and may have changed the size, so show the row as it now stands.
                setEditTarget((open) => (open && open.id === saved.id ? { ...open, ...saved } : open))
            }
            queryClient.invalidateQueries({ queryKey: ['shapes'] })
            toast({
                title: overwrite
                    ? t('shapes.replacedTitle', 'Graphic image replaced')
                    : t('shapes.uploadedTitle', 'Graphics uploaded'),
                description: overwrite
                    ? t('shapes.replacedDescription', '{{name}} was replaced everywhere it is used.', { name: file.name })
                    : t('shapes.uploadedDescription', '{{count}} graphic added to the library.', { count: 1 }),
            })
        } catch (err) {
            const e = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
            if (e?.response?.status === 409) {
                // Same dialog the bulk path uses, with one entry. One place where a clash is
                // put to the user, and it is the application's own dialog rather than a native
                // prompt that cannot be styled or translated by us.
                setBulkClashes([{ file, category }])
                return
            }
            toast({
                variant: 'destructive',
                title: t('shapes.uploadFailed', 'Upload failed'),
                description: e?.response?.data?.message || e?.message || t('shapes.tryAgain', 'Please try again.'),
            })
        } finally {
            setUploading(false)
        }
    }

    // Replace keeps the existing filename: it is the S3 key every referrer resolves
    // through, so writing the same key updates them all at once.
    const replaceImage = (file: File) => {
        if (!editTarget) return
        const ext = (editTarget.filename.match(/\.[^.]+$/) || [''])[0]
        const typed = new File([file], editTarget.filename, { type: file.type || (ext === '.svg' ? 'image/svg+xml' : 'image/png') })
        return uploadSingle(typed, true)
    }

    // Usage is fetched when a delete is requested, so the confirmation can say what will be
    // affected before the shape is removed.
    const { data: usage, isFetching: usageLoading } = useQuery({
        queryKey: ['shape-usage', deleteTarget?.id],
        enabled: !!deleteTarget,
        queryFn: async () => {
            const res = await shapesApi.usage(deleteTarget!.id)
            return res.data.data as ShapeUsage
        },
    })

    const del = useMutation({
        mutationFn: (id: number) => shapesApi.remove(id),
        onSuccess: (res) => {
            const { filename, cleared } = res.data.data
            const clearedTotal = Object.values(cleared || {}).reduce((a, b) => a + Number(b || 0), 0)
            queryClient.invalidateQueries({ queryKey: ['shapes'] })
            setDeleteTarget(null)
            toast({
                title: t('shapes.deletedTitle', 'Graphic deleted'),
                description: clearedTotal > 0
                    ? t('shapes.deletedAndClearedDescription', '{{filename}} removed and cleared from {{count}} item.', { filename, count: clearedTotal })
                    : t('shapes.deletedDescription', '{{filename}} removed from the library.', { filename }),
            })
        },
        onError: (err: any) => {
            toast({
                variant: 'destructive',
                title: t('shapes.deleteErrorTitle', 'Could not delete graphic'),
                description: err?.response?.data?.message || err?.message || t('shapes.tryAgain', 'Please try again.'),
            })
        },
    })

    const save = useMutation({
        mutationFn: (vars: { id: number; category: string; description: string; tags: string }) =>
            shapesApi.update(vars.id, { category: vars.category, description: vars.description, tags: vars.tags }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shapes'] })
            setEditTarget(null)
            toast({ title: t('shapes.savedTitle', 'Graphic updated') })
        },
        onError: (err: any) => {
            toast({
                variant: 'destructive',
                title: t('shapes.saveErrorTitle', 'Could not update graphic'),
                description: err?.response?.data?.message || err?.message || t('shapes.tryAgain', 'Please try again.'),
            })
        },
    })

    // A blocking referrer is a type row that renders many objects — the API refuses the delete
    // rather than clearing it, so the dialog must not offer a button that is going to 409.
    const blocked = !!usage?.blocking?.length

    const openEdit = (shape: ShapeItem) => {
        setEditTarget(shape)
        setEditCategory(shape.category || '')
        setEditDescription(shape.description || '')
        setEditTags(shape.tags || '')
    }

    const editShownSize = editTarget?.width && editTarget?.height ? { width: editTarget.width, height: editTarget.height } : null
    // The dialog is opened from a card, so that card's image has already reported its pixels.
    const editZoom = editTarget && editShownSize && !isVectorFilename(editTarget.filename) && loadedPixels[editTarget.id]
        ? sharpUpToZoom(loadedPixels[editTarget.id], editShownSize)
        : null

    return (
        <>
            <div className="container mx-auto p-6 space-y-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold flex items-center gap-2">
                            <ShapesIcon className="h-7 w-7" />
                            {t('shapes.title', 'Graphics')}
                        </h1>
                        <p className="text-muted-foreground mt-1 max-w-2xl">
                            {t('shapes.subtitle', 'The shared graphic library. Graphics are managed here in the same way as icons, and are drawn on the visualisation in place of node cards.')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative max-w-sm flex-1 min-w-[220px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder={t('shapes.search', 'Search graphics by name, description or tag…')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    {canCreate && (
                        <input
                            ref={fileInput}
                            type="file"
                            accept={SHAPE_ACCEPT}
                            multiple
                            className="hidden"
                            onChange={(e) => handleFiles(e.target.files)}
                        />
                    )}
                    <Select value={workingCategory || '__all__'} onValueChange={(v) => setWorkingCategory(v === '__all__' ? '' : v)}>
                        <SelectTrigger className="w-[220px]">
                            <SelectValue placeholder={t('shapes.allCategories', 'All categories')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">{t('shapes.allCategories', 'All categories')}</SelectItem>
                            {categories.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {canCreate && (<>
                        <Button
                            onClick={() => fileInput.current?.click()}
                            disabled={uploading || !workingCategory}
                            title={!workingCategory ? t('shapes.pickCategoryFirst', 'Choose a category first — a bulk upload has to land somewhere.') : undefined}
                        >
                            {uploading ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Upload className="h-4 w-4 mr-2" />
                            )}
                            {workingCategory
                                ? t('shapes.uploadTo', 'Upload to {{category}}', { category: workingCategory })
                                : t('shapes.upload', 'Upload graphics')}
                        </Button>
                        <Button onClick={() => setAddOpen(true)} disabled={uploading}>
                            <Plus className="h-4 w-4 mr-2" />
                            {t('shapes.add', 'Add graphic')}
                        </Button>
                    </>)}
                    <Button variant="outline" onClick={handleExport} disabled={exporting}>
                        {exporting ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4 mr-2" />
                        )}
                        {t('shapes.exportAll', 'Export all graphics')}
                    </Button>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        {t('shapes.loading', 'Loading graphics…')}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[200px] gap-2 text-sm text-muted-foreground">
                        {search
                            ? t('shapes.noSearchResults', 'No graphics match your search.')
                            : t('shapes.empty', 'No graphics in the library yet. Upload one to get started.')}
                    </div>
                ) : (
                    <>
                        <p className="text-xs text-muted-foreground">{t('shapes.shapeCount', '{{count}} graphic', { count: filtered.length })}</p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                            {filtered.map((shape) => (
                                <div
                                    key={shape.id}
                                    className="group relative flex flex-col items-center gap-1.5 rounded-lg border p-3 transition-colors hover:bg-accent/40"
                                >
                                    <EntityIcon
                                        src={versioned(buildShapePath(shape.filename), imageVersion[shape.filename])}
                                        alt={shape.filename}
                                        className="h-14 w-14"
                                        onLoadedSize={(size) => setLoadedPixels((seen) => (
                                            seen[shape.id]?.width === size.width && seen[shape.id]?.height === size.height
                                                ? seen
                                                : { ...seen, [shape.id]: size }
                                        ))}
                                    />
                                    <span className="w-full truncate text-center text-xs font-medium" title={shape.filename}>
                                        {graphicName(shape)}
                                    </span>
                                    <Badge variant="secondary" className="text-[10px] font-normal">
                                        {shape.category || t('shapes.uncategorised', 'Uncategorised')}
                                    </Badge>
                                    {shape.tags && (
                                        <span className="w-full truncate text-center text-[10px] text-muted-foreground" title={shape.tags}>
                                            {shape.tags}
                                        </span>
                                    )}
                                    {(() => {
                                        // ONLY when it matters. A card that says something about every
                                        // graphic says nothing about any of them, so a file that holds up
                                        // to the zoom people actually use gets no badge at all.
                                        const z = blurZoom(shape, loadedPixels[shape.id])
                                        return z !== null && sharpness(z) === 'blurs' ? (
                                            <span
                                                className="w-full truncate text-center text-[10px] text-amber-700 dark:text-amber-400"
                                                data-testid="graphic-blur-zoom"
                                                title={t('shapes.cardBlursPastHelp', 'The file has too few pixels for the size it draws at. Replace it with a larger image; it will still draw at this size.')}
                                            >
                                                {t('shapes.cardBlursPast', 'Blurs past {{zoom}}× zoom', { zoom: formatZoom(z, i18n.language) })}
                                            </span>
                                        ) : null
                                    })()}
                                    {(canUpdate || canDelete) && (
                                        <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
                                            {canUpdate && (
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(shape)}
                                                    className="rounded-md p-1 text-muted-foreground hover:bg-accent focus:opacity-100"
                                                    title={t('shapes.edit', 'Edit graphic')}
                                                    aria-label={t('shapes.editNamed', 'Edit {{name}}', { name: shape.filename })}
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    type="button"
                                                    onClick={() => setDeleteTarget(shape)}
                                                    className="rounded-md p-1 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground focus:opacity-100"
                                                    title={t('shapes.delete', 'Delete graphic')}
                                                    aria-label={t('shapes.deleteNamed', 'Delete {{name}}', { name: shape.filename })}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Edit description / tags */}
            {/* Add a graphic — the same form the classification dialog uses, so what you can
                describe does not depend on which screen you started from. */}
            <Dialog open={addOpen} onOpenChange={(open) => { if (!open) closeAdd() }}>
                <DialogContent className="max-w-md max-h-[88vh] flex flex-col overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>{t('shapes.addTitle', 'Add a graphic')}</DialogTitle>
                        <DialogDescription>
                            {t('shapes.addSubtitle', 'Choose a file or paste an image. SVG keeps its edges when the canvas zooms.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 overflow-auto -mx-1 px-1">
                        <GraphicUploadForm
                            categories={categories}
                            initialCategory={workingCategory}
                            onUploaded={closeAdd}
                            onCancel={closeAdd}
                        />
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={bulkClashes.length > 0} onOpenChange={(open) => { if (!open) setBulkClashes([]) }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('shapes.overwriteTitle', 'That name is already taken')}</DialogTitle>
                        <DialogDescription>
                            {t('shapes.overwriteBulk',
                                '{{count}} of the files you added already exist in the library. Replace them?',
                                { count: bulkClashes.length })}
                        </DialogDescription>
                    </DialogHeader>
                    <ul className="max-h-40 overflow-auto rounded-md border bg-muted/40 p-2 text-xs font-mono">
                        {bulkClashes.map((c) => <li key={c.file.name} className="truncate">{c.file.name}</li>)}
                    </ul>
                    <p className="text-xs text-muted-foreground">
                        {t('shapes.overwriteWarning',
                            'Replacing changes the image everywhere this graphic is already used, on every classification it is assigned to.')}
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBulkClashes([])} disabled={uploading}>
                            {t('shapes.overwriteSkip', 'Skip them')}
                        </Button>
                        <Button variant="destructive" onClick={replaceClashes} disabled={uploading}>
                            {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {t('shapes.overwriteAction', 'Replace it')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open && !save.isPending) setEditTarget(null) }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {t('shapes.editTitle', 'Edit “{{name}}”', { name: editTarget?.filename || '' })}
                        </DialogTitle>
                        <DialogDescription>
                            {t('shapes.editSubtitle', 'Description and tags are what the search box matches on.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="shape-description">{t('shapes.description', 'Description')}</Label>
                            <Input
                                id="shape-description"
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                placeholder={t('shapes.descriptionPlaceholder', 'What this graphic represents')}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('shapes.categoryLabel', 'Category')}</Label>
                            <Select value={editCategory} onValueChange={setEditCategory}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t('shapes.categoryPlaceholder', 'Which category this graphic belongs in')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-muted-foreground">
                                {t('shapes.categoryHelp', 'A category, not a classification — one network graphic serves every network classification. Use tags below for the specifics (cisco, switch, vm, windows).')}
                            </p>
                        </div>

                        {editShownSize ? (
                            <div className="space-y-1">
                                <p className="text-xs text-muted-foreground" data-testid="shape-drawn-size">
                                    {t('shapes.drawnSizeLabel', 'Size on the canvas')}
                                    {' · '}
                                    <span className="tabular-nums">{drawnBox(editShownSize).width} × {drawnBox(editShownSize).height} px</span>
                                </p>
                                {editZoom !== null && (
                                    <div className={`flex items-center gap-1.5 text-[11px] ${sharpness(editZoom) === 'blurs' ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                                        {sharpness(editZoom) === 'blurs' ? (
                                            <>
                                                {t('shapes.cardBlursPast', 'Blurs past {{zoom}}× zoom', { zoom: formatZoom(editZoom, i18n.language) })}
                                                <SizeInfo
                                                    box={drawnBox(editShownSize)}
                                                    needed={recommendedPixels(editShownSize)}
                                                    zoom={editZoom}
                                                />
                                            </>
                                        ) : sharpness(editZoom) === 'every'
                                            ? t('shapes.sharpAtEveryZoom', 'Sharp at every zoom.')
                                            : t('shapes.sharpToZoom', 'Sharp to {{zoom}}× zoom.', { zoom: formatZoom(editZoom, i18n.language) })}
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {/* Replacing the file is an upload: graphics.create, on top of the update that opened this. */}
                        {canCreate && (
                            <div className="space-y-2">
                                <Label>{t('shapes.replaceImage', 'Replace image')}</Label>
                                <ImageIntake onFile={replaceImage} accept={SHAPE_ACCEPT} busy={uploading} />
                                <p className="text-[11px] text-muted-foreground">
                                    {t('shapes.replaceImageHelp', 'Keeps the same filename, so everything already using this graphic picks up the new image.')}
                                </p>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="shape-tags">{t('shapes.tags', 'Tags')}</Label>
                            <Textarea
                                id="shape-tags"
                                value={editTags}
                                onChange={(e) => setEditTags(e.target.value)}
                                rows={3}
                                placeholder={t('shapes.tagsPlaceholder', 'Comma-separated keywords')}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditTarget(null)} disabled={save.isPending}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={() => editTarget && save.mutate({
                                id: editTarget.id, category: editCategory, description: editDescription, tags: editTags,
                            })}
                            disabled={save.isPending}
                        >
                            {save.isPending ? t('shapes.saving', 'Saving…') : t('common.save', 'Save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation — usage-aware */}
            <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open && !del.isPending) setDeleteTarget(null) }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {t('shapes.deleteConfirmTitle', 'Delete “{{name}}”?', { name: deleteTarget ? graphicName(deleteTarget) : '' })}
                        </DialogTitle>
                    </DialogHeader>

                    {usageLoading ? (
                        <div className="flex items-center justify-center py-6 text-muted-foreground">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            {t('shapes.checkingUsage', 'Checking where this graphic is used…')}
                        </div>
                    ) : usage ? (
                        <div className="space-y-3 text-sm">
                            {blocked ? (
                                // Shown instead of the delete warning, not alongside it: the delete
                                // is not going to happen, so offering the usual "those items keep
                                // their data" reassurance would describe an outcome that never
                                // occurs.
                                <>
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                                        <span>{t('shapes.blockedWarning', 'This graphic can’t be deleted while it is assigned to:')}</span>
                                    </div>
                                    <ul className="ml-6 list-disc space-y-0.5">
                                        {usage.blocking.map((b) => (
                                            <li key={b.label}><b>{b.count}</b> {b.label}</li>
                                        ))}
                                    </ul>
                                    <p className="text-muted-foreground">
                                        {t('shapes.blockedHelp', 'Reassign those to another graphic first. Removing it here would silently change every object in them at once.')}
                                    </p>
                                </>
                            ) : usage.inUse ? (
                                <>
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                                        <span>{t('shapes.inUseWarning', 'This graphic is in use. Deleting it will remove it from:')}</span>
                                    </div>
                                    <ul className="ml-6 list-disc space-y-0.5">
                                        {Object.entries(usage.counts)
                                            .filter(([, n]) => Number(n) > 0)
                                            .map(([label, n]) => (
                                                <li key={label}><b>{n}</b> {label}</li>
                                            ))}
                                    </ul>
                                    <p className="text-muted-foreground">
                                        {t('shapes.usageKeepData', 'Those items keep all their data but will show no graphic until you assign a new one.')}
                                    </p>
                                </>
                            ) : usage.noReferrersConfigured ? (
                                // Said plainly rather than as "not in use": nothing references shapes
                                // at all yet, so claiming it was checked would overstate what we know.
                                <p className="text-muted-foreground">
                                    {t('shapes.noReferrers', 'Nothing references this graphic yet. The file and its library entry will be removed.')}
                                </p>
                            ) : (
                                <p className="text-muted-foreground">
                                    {t('shapes.notInUse', 'This graphic isn’t used by anything. It will be removed from the library.')}
                                </p>
                            )}
                        </div>
                    ) : null}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={del.isPending}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => deleteTarget && del.mutate(deleteTarget.id)}
                            disabled={del.isPending || usageLoading || blocked}
                        >
                            {del.isPending ? t('shapes.deleting', 'Deleting…') : t('shapes.delete', 'Delete graphic')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
